import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, MessageCircle, Share2, Play, Volume2, VolumeX, ThumbsUp, Heart } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { fileUrl } from "@/lib/uploads";
import { getInitials, avatarColor } from "@/lib/avatar";
import { REACTIONS } from "@/components/social/reactions";
import CommentList from "@/components/social/CommentList";
import ShareSheet from "@/components/shared/ShareSheet";
import { useI18n } from "@/context/I18nContext";

const fmtCount = (n) => (n >= 1000 ? (n / 1000).toFixed(1).replace(/\.0$/, "") + "K" : String(n || 0));

// A single full-screen vertical video slide (Shorts / X style).
function Reel({ reel, active, isLast, muted, onToggleMute, onForceMute, onOpenProfile, onComment, onShare, onEnded }) {
  const videoRef = useRef(null);
  const barRef = useRef(null);
  const lastTapRef = useRef(0);
  const singleTimerRef = useRef(null);
  const scrubbingRef = useRef(false);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0); // 0..1
  const [burst, setBurst] = useState(null); // {id, x, y}
  const [scrub, setScrub] = useState(null); // {ratio, x} while dragging the progress bar
  const [dur, setDur] = useState(0);
  const [p, setP] = useState(reel.post);
  const base = p.reshared || p;
  const caption = base.text || "";

  useEffect(() => { if (videoRef.current) videoRef.current.muted = muted; }, [muted]);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    if (active) {
      el.play().then(() => setPlaying(true)).catch(() => {
        // Autoplay with sound is blocked without a fresh gesture on swipe → retry muted
        // so the next video always starts playing.
        try { el.muted = true; onForceMute && onForceMute(); } catch (e) {}
        el.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
      });
    } else {
      el.pause();
      try { el.currentTime = 0; } catch (e) {}
      setProgress(0);
      setPlaying(false);
    }
  }, [active]);

  const toggle = () => {
    const el = videoRef.current;
    if (!el) return;
    if (el.paused) { el.play().then(() => setPlaying(true)).catch(() => {}); }
    else { el.pause(); setPlaying(false); }
  };

  const myMeta = REACTIONS.find((r) => r.type === p.my_reaction);
  const total = p.reactions_total || 0;
  const like = async () => {
    try { const { data } = await api.post(`/social/posts/${p.id}/react`, { type: p.my_reaction || "like" }); setP(data); } catch (e) {}
  };

  // Double-tap the video to like it (with a heart burst); single tap toggles play.
  const onVideoTap = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left, y = e.clientY - rect.top;
    const now = Date.now();
    if (now - lastTapRef.current < 300) {
      clearTimeout(singleTimerRef.current);
      lastTapRef.current = 0;
      setBurst({ id: now, x, y });
      if (!p.my_reaction) like();
    } else {
      lastTapRef.current = now;
      clearTimeout(singleTimerRef.current);
      singleTimerRef.current = setTimeout(() => toggle(), 300);
    }
  };

  const seekTo = (clientX) => {
    const el = videoRef.current, bar = barRef.current;
    if (!el || !bar || !el.duration) return;
    const rect = bar.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    el.currentTime = ratio * el.duration;
    setProgress(ratio);
    setScrub({ ratio, x: rect.left + ratio * rect.width });
  };
  const onBarDown = (e) => { e.stopPropagation(); scrubbingRef.current = true; try { e.currentTarget.setPointerCapture(e.pointerId); } catch (er) {} seekTo(e.clientX); };
  const onBarMove = (e) => { if (scrubbingRef.current) { e.stopPropagation(); seekTo(e.clientX); } };
  const onBarUp = (e) => { scrubbingRef.current = false; try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (er) {} setScrub(null); };
  const fmtTime = (s) => { s = Math.max(0, Math.floor(s || 0)); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`; };

  return (
    <div className="relative h-full w-full snap-start shrink-0 bg-black flex items-center justify-center" data-testid={`reel-${p.id}`}>
      <video ref={videoRef} src={fileUrl(reel.url)} poster={reel.poster ? fileUrl(reel.poster) : undefined}
        loop={isLast} playsInline preload="metadata" onClick={onVideoTap}
        onLoadedMetadata={(e) => { if (e.target.duration && isFinite(e.target.duration)) setDur(e.target.duration); }}
        onTimeUpdate={(e) => { if (!scrubbingRef.current && e.target.duration) setProgress(e.target.currentTime / e.target.duration); }}
        onEnded={() => { if (active && !isLast) onEnded && onEnded(); }}
        className="h-full w-full object-cover" />

      {/* Double-tap heart burst */}
      <AnimatePresence>
        {burst && (
          <motion.div key={burst.id} className="absolute pointer-events-none z-30" style={{ left: burst.x, top: burst.y }}
            initial={{ scale: 0, opacity: 0.9, x: "-50%", y: "-50%" }}
            animate={{ scale: 1.25, opacity: 1 }} exit={{ scale: 1.6, opacity: 0, y: "-90%" }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            onAnimationComplete={() => setBurst(null)} data-testid={`reel-heart-burst-${p.id}`}>
            <Heart size={92} className="text-[var(--rc-gold)] drop-shadow-lg" fill="currentColor" />
          </motion.div>
        )}
      </AnimatePresence>

      {!playing && (
        <button onClick={toggle} className="absolute inset-0 grid place-items-center" aria-label="Play" data-testid={`reel-play-${p.id}`}>
          <span className="h-16 w-16 grid place-items-center rounded-full bg-black/50"><Play size={30} className="text-white ml-1" fill="white" /></span>
        </button>
      )}

      {/* Right action rail */}
      <div className="absolute right-3 bottom-28 flex flex-col items-center gap-5 z-10">
        <button onClick={like} data-testid={`reel-like-${p.id}`} className="flex flex-col items-center gap-1 text-white">
          <span className={`h-11 w-11 grid place-items-center rounded-full ${myMeta ? "bg-[var(--rc-gold)] text-black" : "bg-white/15 backdrop-blur"}`}>
            {myMeta ? <span className="text-xl leading-none">{myMeta.emoji}</span> : <ThumbsUp size={22} />}
          </span>
          <span className="text-xs font-medium">{fmtCount(total)}</span>
        </button>
        <button onClick={() => onComment(p)} data-testid={`reel-comment-${p.id}`} className="flex flex-col items-center gap-1 text-white">
          <span className="h-11 w-11 grid place-items-center rounded-full bg-white/15 backdrop-blur"><MessageCircle size={22} /></span>
          <span className="text-xs font-medium">{fmtCount(p.comment_count || 0)}</span>
        </button>
        <button onClick={() => onShare(p)} data-testid={`reel-share-${p.id}`} className="flex flex-col items-center gap-1 text-white">
          <span className="h-11 w-11 grid place-items-center rounded-full bg-white/15 backdrop-blur"><Share2 size={22} /></span>
          <span className="text-xs font-medium">{fmtCount(p.share_count || 0)}</span>
        </button>
        <button onClick={onToggleMute} data-testid={`reel-mute-${p.id}`} className="h-11 w-11 grid place-items-center rounded-full bg-white/15 backdrop-blur text-white">
          {muted ? <VolumeX size={20} /> : <Volume2 size={20} />}
        </button>
      </div>

      {/* Bottom author + caption */}
      <div className="absolute left-0 right-16 bottom-0 p-4 pb-8 bg-gradient-to-t from-black/70 to-transparent z-10">
        <button onClick={() => onOpenProfile && onOpenProfile(base.author_id)} data-testid={`reel-author-${p.id}`} className="flex items-center gap-2 mb-2">
          <span className="h-9 w-9 rounded-2xl grid place-items-center text-[var(--rc-gold)] text-sm font-semibold border-2 border-[var(--rc-gold)] overflow-hidden shrink-0"
            style={{ backgroundColor: base.author_avatar ? "var(--rc-bg)" : avatarColor(base.author_name) }}>
            {base.author_avatar ? <img src={base.author_avatar} alt="" className="h-full w-full object-cover" /> : getInitials(base.author_name)}
          </span>
          <span className="text-white font-semibold text-sm">{base.author_name}</span>
        </button>
        {caption && <p className="text-white/90 text-sm line-clamp-2 [overflow-wrap:anywhere]">{caption}</p>}
      </div>

      {/* Time bubble while scrubbing */}
      {scrub && (
        <div className="absolute z-40 -translate-x-1/2 bottom-9 pointer-events-none" style={{ left: scrub.x }} data-testid={`reel-scrub-bubble-${p.id}`}>
          <div className="rounded-lg bg-black/80 px-2.5 py-1 text-xs font-semibold tabular-nums text-white shadow-lg">
            {fmtTime(scrub.ratio * dur)} <span className="text-white/50">/ {fmtTime(dur)}</span>
          </div>
        </div>
      )}

      {/* Progress bar — tap / drag anywhere to scrub (X-style) */}
      <div ref={barRef} onPointerDown={onBarDown} onPointerMove={onBarMove} onPointerUp={onBarUp} onClick={(e) => e.stopPropagation()}
        data-testid={`reel-progress-${p.id}`} className="absolute left-0 right-0 bottom-0 z-30 pt-4 pb-1.5 px-0 cursor-pointer touch-none">
        <div className={`mx-3 rounded-full bg-white/25 overflow-visible relative transition-[height] ${scrub ? "h-[5px]" : "h-[3px]"}`}>
          <div className="h-full bg-white rounded-full" style={{ width: `${Math.round(progress * 100)}%` }} data-testid={`reel-progress-fill-${p.id}`} />
          {scrub && <span className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-3 w-3 rounded-full bg-white shadow" style={{ left: `${Math.round(scrub.ratio * 100)}%` }} />}
        </div>
      </div>
    </div>
  );
}

// Full-screen vertical video pager. Swipe up / down to move between videos.
export default function ReelsViewer({ reels, startIndex = 0, onClose, onOpenProfile }) {
  const { t } = useI18n();
  const containerRef = useRef(null);
  const [active, setActive] = useState(startIndex);
  const [muted, setMuted] = useState(false); // fullscreen reels play with sound by default
  const [commentsFor, setCommentsFor] = useState(null);
  const [shareFor, setShareFor] = useState(null);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (el) el.scrollTop = startIndex * el.clientHeight;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onScroll = (e) => {
    const el = e.target;
    const idx = Math.round(el.scrollTop / el.clientHeight);
    setActive((a) => (a === idx ? a : idx));
  };

  // Auto-advance to the next reel when the active one finishes (hands-free).
  const goNext = () => {
    const c = containerRef.current;
    if (!c) return;
    const idx = Math.round(c.scrollTop / c.clientHeight);
    if (idx < reels.length - 1) c.scrollTo({ top: (idx + 1) * c.clientHeight, behavior: "smooth" });
  };

  return createPortal(
    <div className="fixed inset-0 z-[70] bg-black" data-testid="reels-viewer">
      <button onClick={onClose} data-testid="reels-close"
        className="absolute top-4 left-4 z-20 h-10 w-10 grid place-items-center rounded-full bg-black/50 text-white rc-safe-top">
        <X size={22} />
      </button>

      <div ref={containerRef} onScroll={onScroll} data-testid="reels-scroll"
        className="h-full w-full overflow-y-scroll snap-y snap-mandatory no-scrollbar overscroll-contain">
        {reels.map((r, i) => (
          <div key={`${r.post.id}-${i}`} className="h-full w-full">
            <Reel reel={r} active={i === active} isLast={i === reels.length - 1} muted={muted} onToggleMute={() => setMuted((m) => !m)} onForceMute={() => setMuted(true)}
              onOpenProfile={(id) => { onClose(); onOpenProfile && onOpenProfile(id); }}
              onComment={(p) => setCommentsFor(p)} onShare={(p) => setShareFor(p)} onEnded={goNext} />
          </div>
        ))}
      </div>

      {commentsFor && createPortal(
        <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/60" onClick={() => setCommentsFor(null)} data-testid="reel-comments-sheet">
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-xl bg-[var(--rc-bg)] rounded-t-3xl pt-3 pb-4 rc-safe-bottom max-h-[75vh] overflow-y-auto no-scrollbar">
            <div className="mx-auto mb-2 h-1.5 w-12 rounded-full bg-[var(--rc-border)]" />
            <div className="flex items-center justify-between px-4 mb-2">
              <h3 className="font-heading text-lg">{t("s_comments") || "Comments"}</h3>
              <button onClick={() => setCommentsFor(null)} data-testid="reel-comments-close" className="h-8 w-8 grid place-items-center rounded-full text-[var(--rc-muted)] hover:bg-[var(--rc-surface)]"><X size={18} /></button>
            </div>
            <CommentList postId={commentsFor.id} onOpenProfile={(id) => { setCommentsFor(null); onClose(); onOpenProfile && onOpenProfile(id); }} />
          </div>
        </div>, document.body)}

      {shareFor && <ShareSheet kind="post" item={shareFor} onClose={() => setShareFor(null)} />}
    </div>,
    document.body
  );
}
